import React, { createContext, useContext, useMemo, useCallback } from 'react';
import { validateLectureForEvent, testEventSchema, isStepDataComplete } from '../validation/lectures.js';
import { useFormContext } from './FormProvider';

const ValidationContext = createContext();

export function ValidationProvider({ children, eventConfig = testEventSchema }) {
  const { formData } = useFormContext();
  
  // Validate current form data against schema
  const validateCurrentData = useCallback(() => {
    return validateLectureForEvent(formData, eventConfig);
  }, [formData, eventConfig]);
  
  // Get validation result
  const validationResult = useMemo(() => {
    return validateCurrentData();
  }, [validateCurrentData]);
  
  // Get validation errors by field
  const getFieldValidation = useCallback((fieldPath) => {
    if (!validationResult.errors) return null;
    
    return validationResult.errors.find(error => error.field === fieldPath);
  }, [validationResult.errors]);
  
  // Get validation errors by step
  const getStepValidation = useCallback((stepId) => {
    if (!validationResult.errors) return { isValid: true, errors: [] };
    
    // Map step IDs to schema field paths
    const stepFieldMapping = {
      'lecture_type': ['type'],
      'conditions': ['conditions'],
      'speakers': ['speakers'],
      'language': ['language'],
      'title': ['title'],
      'description': ['content'],
      'topics': ['topics'],
      'image': ['image'],
      'handout': ['handout'],
      'presentation': ['presentation'],
      'subjects': ['subjects'],
      'topics': ['topics'],
      'eventLocation': ['eventLocation'],
      'targetAudience': ['targetAudience']
    };
    
    const stepFields = stepFieldMapping[stepId] || [];
    const stepErrors = validationResult.errors.filter(error => 
      stepFields.some(field => error.field.startsWith(field))
    );
    
    return {
      isValid: stepErrors.length === 0,
      errors: stepErrors
    };
  }, [validationResult.errors]);
  
  // Get required fields for a step based on event config
  const getStepRequiredFields = useCallback((stepId) => {
    const stepFieldMapping = {
      'lecture_type': ['type'],
      'conditions': ['conditions'],
      'speakers': ['speakers'], 
      'language': ['language'],
      'title': ['title'],
      'description': ['content'],
      'topics': ['topics'],
      'image': ['image'],
      'handout': ['handout'],
      'presentation': ['presentation'],
      'subjects': ['subjects'],
      'topics': ['topics'],
      'eventLocation': ['eventLocation'],
      'targetAudience': ['targetAudience']
    };
    
    const stepFields = stepFieldMapping[stepId] || [];
    return stepFields.filter(field => eventConfig.requiredFields?.includes(field));
  }, [eventConfig.requiredFields]);
  
  // Get conditions validation info specifically
  const getConditionsValidation = useCallback(() => {
    const conditionsError = getFieldValidation('conditions');
    
    // For dynamic conditions, check based on lecture type and event config
    let requiredConditions = 0;
    let availableConditions = 0;
    
    if (eventConfig.eventConditions && formData.type) {
      const applicableConditions = eventConfig.eventConditions.filter(condition => 
        condition.applicableToTypes.includes(formData.type)
      );
      availableConditions = applicableConditions.length;
      requiredConditions = applicableConditions.filter(c => c.required).length;
    } else {
      // Fallback to original logic if no dynamic conditions
      requiredConditions = 4;
    }
    
    const currentConditions = formData.conditions?.filter(c => c.accepted)?.length || 0;
    
    return {
      required: requiredConditions,
      current: currentConditions,
      available: availableConditions,
      isValid: !conditionsError && currentConditions >= requiredConditions,
      error: conditionsError?.message,
      isRequired: eventConfig.requiredFields?.includes('conditions') || false,
      lectureType: formData.type
    };
  }, [getFieldValidation, eventConfig.requiredFields, eventConfig.eventConditions, formData.conditions, formData.type]);
  
  // Check if form is ready to save
  const isFormReadyToSave = useCallback(() => {
    return validationResult.success;
  }, [validationResult.success]);
  
  // Get all validation issues grouped by category
  const getValidationSummary = useCallback(() => {
    if (validationResult.success) {
      return {
        isValid: true,
        totalErrors: 0,
        errorsByStep: {},
        criticalErrors: []
      };
    }
    
    const errorsByStep = {};
    const criticalErrors = [];
    
    validationResult.errors?.forEach(error => {
      // Determine which step this error belongs to
      let stepId = 'unknown';
      if (error.field.startsWith('type')) stepId = 'lecture_type';
      else if (error.field.startsWith('conditions')) stepId = 'conditions';
      else if (error.field.startsWith('speakers')) stepId = 'speakers';
      else if (error.field.startsWith('title')) stepId = 'title';
      else if (error.field.startsWith('content')) stepId = 'description';
      else if (error.field.startsWith('image')) stepId = 'image';
      else if (error.field.startsWith('subjects')) stepId = 'subjects';
      else if (error.field.startsWith('topics')) stepId = 'topics';
      else if (error.field.startsWith('eventLocation')) stepId = 'eventLocation';
      else if (error.field.startsWith('targetAudience')) stepId = 'targetAudience';
      
      if (!errorsByStep[stepId]) errorsByStep[stepId] = [];
      errorsByStep[stepId].push(error);
      
      // Mark critical errors (required fields)
      if (eventConfig.requiredFields?.some(field => error.field.startsWith(field))) {
        criticalErrors.push(error);
      }
    });
    
    return {
      isValid: false,
      totalErrors: validationResult.errors?.length || 0,
      errorsByStep,
      criticalErrors
    };
  }, [validationResult, eventConfig.requiredFields]);
  
  // Check if step data is complete (for auto-completion)
  const isStepComplete = useCallback((stepId) => {
    return isStepDataComplete(stepId, eventConfig, formData);
  }, [eventConfig, formData]);

  const contextValue = {
    // Core validation
    validationResult,
    validateCurrentData,
    
    // Field-level validation
    getFieldValidation,
    
    // Step-level validation
    getStepValidation,
    getStepRequiredFields,
    
    // Step completion check
    isStepComplete,
    
    // Specific validations
    getConditionsValidation,
    
    // Overall form state
    isFormReadyToSave,
    getValidationSummary,
    
    // Event configuration
    eventConfig
  };
  
  return (
    <ValidationContext.Provider value={contextValue}>
      {children}
    </ValidationContext.Provider>
  );
}

export function useValidation() {
  const context = useContext(ValidationContext);
  if (!context) {
    throw new Error('useValidation must be used within a ValidationProvider');
  }
  return context;
}